import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/api/auth/register', data),
  login: (data) => api.post('/api/auth/login', data),
  me: () => api.get('/api/auth/me'),
  forgotPassword: (email) => api.post('/api/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/api/auth/reset-password', { token, new_password: newPassword }),
  verifyResetToken: (token) => api.get(`/api/auth/verify-reset-token/${token}`),
};

// Users
export const usersAPI = {
  // role: optional filter ('admin'|'trainer'|'client'|'master_admin')
  // org_id: optional — only honored for master_admin; defaults to caller's own org otherwise
  list: (role, org_id) => api.get('/api/users', { params: { role, org_id } }),
  listAvailableClients: () => api.get('/api/users/available-clients'),
  deactivate: (userId) => api.put(`/api/users/${userId}/deactivate`),
};

// Organizations (gyms) — master admin manages all, org admin reads own
export const organizationsAPI = {
  list: () => api.get('/api/admin/organizations'),
  get: (orgId) => api.get(`/api/admin/organizations/${orgId}`),
  create: (data) => api.post('/api/admin/organizations', data),
  update: (orgId, data) => api.put(`/api/admin/organizations/${orgId}`, data),
  delete: (orgId) => api.delete(`/api/admin/organizations/${orgId}`),
  syncFromKeycloak: () => api.post('/api/admin/organizations/sync-from-keycloak'),
  testSmtp: () => api.post('/api/admin/organizations/test-smtp'),
};

// Trainer-Client
export const trainerAPI = {
  assignClient: (clientId) => api.post('/api/trainer/clients', { client_id: clientId }),
  getClients: () => api.get('/api/trainer/clients'),
};

// Sessions
export const sessionsAPI = {
  create: (data) => api.post('/api/sessions', data),
  list: (status) => api.get('/api/sessions', { params: { status } }),
  getPending: () => api.get('/api/sessions/pending'),
  confirm: (sessionId) => api.post(`/api/sessions/${sessionId}/confirm`),
};

// Gamification Config
export const configAPI = {
  get: () => api.get('/api/admin/gamification/config'),
  create: (data) => api.post('/api/admin/gamification/config', data),
  getHistory: () => api.get('/api/admin/gamification/config/history'),
};

// Points
export const pointsAPI = {
  getLedger: (limit = 50) => api.get('/api/points/ledger', { params: { limit } }),
  getBalance: () => api.get('/api/points/balance'),
  getBadges: () => api.get('/api/points/badges'),
  adjust: (userId, pointsDelta, reason) => api.post('/api/admin/points/adjust', null, {
    params: { user_id: userId, points_delta: pointsDelta, reason }
  }),
};

// Rewards
export const rewardsAPI = {
  list: () => api.get('/api/rewards'),
  create: (data) => api.post('/api/admin/rewards', data),
  redeem: (rewardId) => api.post(`/api/rewards/${rewardId}/redeem`),
  getRedemptions: () => api.get('/api/rewards/redemptions'),
  processRedemption: (redemptionId, approved, notes) => 
    api.put(`/api/admin/redemptions/${redemptionId}/process`, null, { params: { approved, notes } }),
};

// Challenges
export const challengesAPI = {
  list: (activeOnly = true) => api.get('/api/challenges', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/api/admin/challenges', data),
  enroll: (challengeId) => api.post(`/api/challenges/${challengeId}/enroll`),
  getEnrollments: () => api.get('/api/challenges/my-enrollments'),
};

// Leaderboard
export const leaderboardAPI = {
  get: (period = 'all_time', limit = 10) => api.get('/api/leaderboard', { params: { period, limit } }),
};

// Suspicious Flags
export const flagsAPI = {
  list: (status) => api.get('/api/admin/flags', { params: { status } }),
  review: (flagId, status, notes) => api.put(`/api/admin/flags/${flagId}/review`, { status, notes }),
};

// Integrity
export const integrityAPI = {
  verify: () => api.get('/api/admin/integrity/verify'),
};

// Dashboard
export const dashboardAPI = {
  admin: () => api.get('/api/admin/dashboard'),
  trainer: () => api.get('/api/trainer/dashboard'),
  client: () => api.get('/api/client/dashboard'),
};

// Teams
export const teamsAPI = {
  list: () => api.get('/api/teams'),
  create: (data) => api.post('/api/teams', data),
  getMyTeam: () => api.get('/api/teams/my-team'),
  getMembers: (teamId) => api.get(`/api/teams/${teamId}/members`),
  invite: (teamId, userId) => api.post(`/api/teams/${teamId}/invite`, { user_id: userId }),
  acceptInvite: (teamId) => api.post(`/api/teams/invites/${teamId}/accept`),
  declineInvite: (teamId) => api.post(`/api/teams/invites/${teamId}/decline`),
  getInvites: () => api.get('/api/teams/invites'),
  leave: (teamId) => api.post(`/api/teams/${teamId}/leave`),
  enrollInChallenge: (challengeId) => api.post(`/api/teams/challenges/${challengeId}/enroll`),
  getLeaderboard: (period = 'all_time') => api.get('/api/teams/leaderboard', { params: { period } }),
};

// Analytics
export const analyticsAPI = {
  getDashboard: (days = 30) => api.get('/api/analytics', { params: { days } }),
  exportCSV: (days = 30) => api.get('/api/analytics/export/csv', { params: { days }, responseType: 'blob' }),
  getClientReport: (clientId, days = 30) => api.get(`/api/analytics/client-report/${clientId}`, { params: { days } }),
  exportClientReportCSV: (clientId, days = 30) => api.get(`/api/analytics/export/client-report/${clientId}/csv`, { params: { days }, responseType: 'blob' }),
};

// Notifications
export const notificationsAPI = {
  list: (params = {}) => api.get('/api/notifications', { params }),
  markRead: (id) => api.post(`/api/notifications/${id}/read`),
  markAllRead: () => api.post('/api/notifications/mark-all-read'),
  getUnreadCount: () => api.get('/api/notifications/unread-count'),
  getPreferences: () => api.get('/api/notifications/preferences'),
  updatePreferences: (patch) => api.put('/api/notifications/preferences', patch),
};

// Insights (muscle engagement + future aggregated analytics)
export const insightsAPI = {
  getMuscleEngagement: (days = 7) =>
    api.get('/api/insights/muscle-engagement', { params: { days } }),
  getCalendarHeatmap: (days = 365) =>
    api.get('/api/insights/calendar-heatmap', { params: { days } }),
  getPRProgression: (days = 365) =>
    api.get('/api/insights/pr-progression', { params: { days } }),
};

// Goals — user-defined body composition / training targets
export const goalsAPI = {
  list: (params = {}) => api.get('/api/goals', { params }),
  create: (data) => api.post('/api/goals', data),
  update: (id, data) => api.put(`/api/goals/${id}`, data),
  delete: (id) => api.delete(`/api/goals/${id}`),
};

// Exercises & PRs
export const exercisesAPI = {
  list: (params = {}) => api.get('/api/exercises', { params }),
  get: (exerciseId) => api.get(`/api/exercises/${exerciseId}`),
  create: (data) => api.post('/api/exercises', data),
  update: (exerciseId, data) => api.put(`/api/exercises/${exerciseId}`, data),
  delete: (exerciseId) => api.delete(`/api/exercises/${exerciseId}`),
  // Personal Records
  createPR: (data) => api.post('/api/exercises/pr', data),
  getMyPRs: (exerciseId) => api.get('/api/exercises/pr/my', { params: { exercise_id: exerciseId } }),
  getPRHistory: (exerciseId) => api.get(`/api/exercises/pr/${exerciseId}/history`),
  getBestPR: (exerciseId) => api.get(`/api/exercises/pr/best/${exerciseId}`),
};

// Workout Templates & Programs
export const workoutsAPI = {
  // Templates
  listTemplates: (params = {}) => api.get('/api/workouts/templates', { params }),
  getTemplate: (templateId) => api.get(`/api/workouts/templates/${templateId}`),
  createTemplate: (data) => api.post('/api/workouts/templates', data),
  updateTemplate: (templateId, data) => api.put(`/api/workouts/templates/${templateId}`, data),
  deleteTemplate: (templateId) => api.delete(`/api/workouts/templates/${templateId}`),
  // Programs
  listPrograms: (params = {}) => api.get('/api/workouts/programs', { params }),
  getProgram: (programId) => api.get(`/api/workouts/programs/${programId}`),
  createProgram: (data) => api.post('/api/workouts/programs', data),
  enrollInProgram: (programId, data) => api.post(`/api/workouts/programs/${programId}/enroll`, data),
  getMyEnrollments: () => api.get('/api/workouts/programs/enrollments/my'),
  updateProgramProgress: (enrollmentId) => api.post(`/api/workouts/programs/enrollments/${enrollmentId}/progress`),
  // Workout Logs
  logWorkout: (data) => api.post('/api/workouts/logs', data),
  getWorkoutLogs: (params = {}) => api.get('/api/workouts/logs', { params }),
  getWorkoutLog: (logId) => api.get(`/api/workouts/logs/${logId}`),
  deleteWorkoutLog: (logId) => api.delete(`/api/workouts/logs/${logId}`),
  getWorkoutSummary: (days = 30) => api.get('/api/workouts/logs/summary/stats', { params: { days } }),
  getPlannedVsActual: (logId) => api.get(`/api/workouts/logs/${logId}/comparison`),
  getClientWorkoutLogs: (clientId, params = {}) => api.get(`/api/workouts/logs/client/${clientId}`, { params }),
};

// Progress Tracking
export const progressAPI = {
  // Body Metrics
  recordMetric: (data) => api.post('/api/progress/metrics', data),
  getMetrics: (params = {}) => api.get('/api/progress/metrics', { params }),
  getMetricHistory: (metricType, days = 365) => api.get(`/api/progress/metrics/history/${metricType}`, { params: { days } }),
  getLatestMetrics: () => api.get('/api/progress/metrics/latest'),
  // Photos
  uploadPhoto: (data) => api.post('/api/progress/photos', data),
  getPhotos: (photoType) => api.get('/api/progress/photos', { params: { photo_type: photoType } }),
  deletePhoto: (photoId) => api.delete(`/api/progress/photos/${photoId}`),
  // Summary
  getSummary: (days = 90) => api.get('/api/progress/summary', { params: { days } }),
  getClientSummary: (clientId, days = 90) => api.get(`/api/progress/client/${clientId}/summary`, { params: { days } }),
};

// Workout Scheduling
export const scheduleAPI = {
  createSchedule: (data) => api.post('/api/schedule', data),
  getSchedules: (params = {}) => api.get('/api/schedule', { params }),
  getSchedule: (scheduleId) => api.get(`/api/schedule/${scheduleId}`),
  updateSchedule: (scheduleId, data) => api.put(`/api/schedule/${scheduleId}`, data),
  deleteSchedule: (scheduleId) => api.delete(`/api/schedule/${scheduleId}`),
  markComplete: (scheduleId, workoutLogId) => api.post(`/api/schedule/${scheduleId}/complete`, null, { params: { workout_log_id: workoutLogId } }),
  getWeeklySchedule: (weekOffset = 0) => api.get('/api/schedule/week', { params: { week_offset: weekOffset } }),
  getTodaysWorkouts: () => api.get('/api/schedule/today'),
  // Reminders
  getPendingReminders: () => api.get('/api/schedule/reminders/pending'),
  getUpcomingReminders: (hours = 24) => api.get('/api/schedule/reminders/upcoming', { params: { hours } }),
  markReminderRead: (reminderId) => api.post(`/api/schedule/reminders/${reminderId}/read`),
  markAllRemindersRead: () => api.post('/api/schedule/reminders/read-all'),
};

// Social Feed
export const feedAPI = {
  createPost: (data) => api.post('/api/feed/posts', data),
  getFeed: (params = {}) => api.get('/api/feed/posts', { params }),
  getPost: (postId) => api.get(`/api/feed/posts/${postId}`),
  deletePost: (postId) => api.delete(`/api/feed/posts/${postId}`),
  likePost: (postId) => api.post(`/api/feed/posts/${postId}/like`),
  unlikePost: (postId) => api.delete(`/api/feed/posts/${postId}/like`),
  getComments: (postId) => api.get(`/api/feed/posts/${postId}/comments`),
  addComment: (postId, data) => api.post(`/api/feed/posts/${postId}/comments`, data),
  deleteComment: (postId, commentId) => api.delete(`/api/feed/posts/${postId}/comments/${commentId}`),
};

// Direct Messaging
export const messagesAPI = {
  getConversations: () => api.get('/api/messages/conversations'),
  getConversation: (convId) => api.get(`/api/messages/conversations/${convId}`),
  createConversation: (data) => api.post('/api/messages/conversations', data),
  startConversationWith: (userId) => api.post(`/api/messages/start-with/${userId}`),
  leaveConversation: (convId) => api.delete(`/api/messages/conversations/${convId}`),
  getMessages: (convId, params = {}) => api.get(`/api/messages/conversations/${convId}/messages`, { params }),
  sendMessage: (convId, data) => api.post(`/api/messages/conversations/${convId}/messages`, data),
  markConversationRead: (convId) => api.post(`/api/messages/conversations/${convId}/read`),
  getUnreadCount: () => api.get('/api/messages/unread-count'),
};

// ==================== CONTACT (public landing form) ====================
export const contactAPI = {
  submit: (data) => api.post('/api/contact', data),
};

export default api;
